import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';

// бекенд дає сирий байтовий потік/превʼю
const fileSrc = (id) => `/drive.php?action=img&id=${encodeURIComponent(id)}`;

export default function ImageViewer({ item, lb }) {
    const {
        viewerRef,
        scale, offset, rotation,
        onMouseDown, onMouseMove, onMouseUp,
        onDoubleClick, onTouchStart, onTouchMove, onTouchEnd,
        imgReady, setImgReady,

        close,          // ⬅️ тепер беремо close з lb
        setRotation,
        slideshow, setSlideshow, slideInterval, setSlideInterval,
        prev,
        next,
    } = lb;

    const src = fileSrc(item.id);

    // локальний стан для fullscreen
    const [isFullscreen, setIsFullscreen] = useState(false);

    // refs для double-tap + swipe
    const lastTapRef = useRef(0);
    const touchStartXRef = useRef(0);
    const touchStartYRef = useRef(0);

    const SWIPE_THRESHOLD = 50; // px

    // toggle fullscreen
    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            viewerRef.current?.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    }, [viewerRef]);

    // 👇 Обгортки для touch-ів: double tap (ESC) + swipe
    const handleTouchStart = useCallback(
        (e) => {
            const now = Date.now();
            const delta = now - lastTapRef.current;
            lastTapRef.current = now;

            // якщо два тапи за короткий час — діємо як ESC → закриваємо лайтбокс
            if (delta > 0 && delta < 300) {
                e.stopPropagation();
                if (typeof close === 'function') {
                    close();
                }
                // не викликаємо onDoubleClick, не рухаємо swipe
                return;
            }

            // зберігаємо стартову позицію для swipe
            if (e.touches && e.touches.length === 1) {
                const t = e.touches[0];
                touchStartXRef.current = t.clientX;
                touchStartYRef.current = t.clientY;
            }

            if (typeof onTouchStart === 'function') {
                onTouchStart(e);
            }
        },
        [close, onTouchStart]
    );

    const handleTouchMove = useCallback(
        (e) => {
            if (typeof onTouchMove === 'function') {
                onTouchMove(e);
            }
        },
        [onTouchMove]
    );

    const handleTouchEnd = useCallback(
        (e) => {
            // swipe детекція
            const touch = e.changedTouches && e.changedTouches[0];
            if (touch) {
                const dx = touch.clientX - touchStartXRef.current;
                const dy = touch.clientY - touchStartYRef.current;

                if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
                    // swipe горизонтальний
                    if (dx > 0) {
                        // свайп вправо → попереднє зображення
                        if (typeof prev === 'function') prev();
                    } else {
                        // свайп вліво → наступне зображення
                        if (typeof next === 'function') next();
                    }
                }
            }

            if (typeof onTouchEnd === 'function') {
                onTouchEnd(e);
            }
        },
        [onTouchEnd, prev, next]
    );

    return (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
            {/* Верхня панель керування */}
            <div
                className="
                    absolute -top-[53px] left-1/2 -translate-x-1/2
                    flex flex-wrap items-center justify-center gap-2
                    text-white/90 z-10
                    backdrop-blur-sm rounded-full px-3 py-1.5
                    "
                onClick={(e) => e.stopPropagation()}
            >
                {/* Rotate */}
                <button onClick={(e) => { e.stopPropagation(); setRotation(r => (r - 90) % 360); }}
                    className="text-sm px-3 py-1.5 rounded-full hover:bg-white/20">⟲</button>
                <button onClick={(e) => { e.stopPropagation(); setRotation(r => (r + 90) % 360); }}
                    className="text-sm px-3 py-1.5 rounded-full hover:bg-white/20">⟳</button>
                <button onClick={(e) => { e.stopPropagation(); setRotation(0); }}
                    className="text-sm px-3 py-1.5 rounded-full hover:bg-white/20">Reset</button>

                {/* Slideshow */}
                <button
                    onClick={(e) => { e.stopPropagation(); setSlideshow(s => !s); }}
                    className={`hidden md:text-sm px-3 py-1.5 rounded-full border border-white/20 ${slideshow ? 'bg-white/20' : 'bg-white/10'}`}
                >
                    {slideshow ? 'Pause' : 'Play'}
                </button>
                <label className="hidden md:text-xs opacity-90 ml-1 flex items-center gap-1">
                    Інтервал:
                    <select
                        className="rounded px-1 py-0.5 bg-gray-800 text-white border border-white/30 focus:outline-none focus:ring-1 focus:ring-white"
                        value={slideInterval}
                        onChange={(e) => { e.stopPropagation(); setSlideInterval(Number(e.target.value)); }}
                    >
                        {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(s => <option key={s} value={s}>{s}s</option>)}
                    </select>
                </label>

                {/* Fullscreen */}
                <button
                    onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                    className="text-sm px-3 py-1.5 rounded-full hover:bg-white/20"
                >
                    {isFullscreen ? '⤡ Вийти' : '⤢ На весь екран'}
                </button>
            </div>

            {/* Сам вʼювер */}
            <div
                className="relative overflow-hidden rounded-xl shadow-2xl bg-black/20"
                ref={viewerRef}
                style={{
                    height: isFullscreen ? '100vh' : '78vh',
                    width: isFullscreen ? '100vw' : '92vw',
                    touchAction: 'none',
                    cursor: 'zoom-in',
                }}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onDoubleClick={onDoubleClick}   // ⬅️ desktop double-click як раніше
                onTouchStart={handleTouchStart} // ⬅️ mobile double-tap = ESC (close)
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className={`w-full h-full transition-opacity duration-300 ${imgReady ? 'opacity-100' : 'opacity-0'}`}>
                    <img
                        key={item.id}
                        src={src}
                        alt={item.name || 'Зображення'}
                        className="block object-contain"
                        style={{
                            width: '100%',
                            height: '100%',
                            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotation}deg)`,
                            transformOrigin: 'center center',
                            margin: '0 auto',
                        }}
                        draggable={false}
                        onLoad={() => setImgReady(true)}
                        onError={() => setImgReady(true)}
                    />
                </div>
            </div>
        </div>
    );
}

ImageViewer.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
    }).isRequired,
    lb: PropTypes.object.isRequired,
};
